/**
 * DocsPendingCard — dashboard widget summarising how many candidates are
 * currently at a gate stage with missing employment docs.
 *
 * Silent when the feature is disabled OR when there's nothing pending.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldAlert, ArrowRight } from "lucide-react";

export default function DocsPendingCard() {
    const [state, setState] = useState({ loaded: false, count: 0, enabled: false, items: [] });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/doc-freshness/pending");
                setState({
                    loaded: true,
                    enabled: !!data?.config?.enabled,
                    count: data?.total || 0,
                    items: (data?.items || []).slice(0, 3),
                });
            } catch {
                setState({ loaded: true, count: 0, enabled: false, items: [] });
            }
        })();
    }, []);

    if (!state.loaded) return null;
    if (!state.enabled) return null;
    if (state.count === 0) return null;

    return (
        <div
            data-testid="docs-pending-dashboard-card"
            className="border border-amber-200 bg-amber-50 rounded-lg p-4 flex items-start justify-between gap-4"
        >
            <div className="min-w-0">
                <div className="flex items-center gap-2 text-amber-800 font-semibold">
                    <ShieldAlert className="h-5 w-5" />
                    {state.count} candidate{state.count === 1 ? "" : "s"} pending documents
                </div>
                <div className="text-xs text-amber-900/80 mt-1">
                    At a gate stage but missing joining / relieving letters.
                </div>
                <div className="mt-2 space-y-1">
                    {state.items.map((r) => (
                        <div key={r.candidate_id} className="text-xs">
                            <Link
                                to={`/candidates/${r.candidate_id}`}
                                className="text-amber-900 hover:underline font-medium"
                                data-testid={`docs-pending-dashboard-item-${r.candidate_id}`}
                            >
                                {r.candidate_name}
                            </Link>
                            <span className="text-amber-900/70"> · {r.missing_count} missing</span>
                        </div>
                    ))}
                </div>
            </div>
            <Link
                to="/docs-pending"
                className="text-xs font-mono uppercase tracking-widest text-amber-900 hover:text-amber-700 inline-flex items-center gap-1 shrink-0"
                data-testid="docs-pending-dashboard-cta"
            >
                Review <ArrowRight className="h-3.5 w-3.5" />
            </Link>
        </div>
    );
}
